import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { authApi, saveAuthSession, physioApi } from '../services/api';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

function PhysiotherapistRegistrationPage() {
  const navigate = useNavigate();
  const locationState = useLocation();
  const [searchParams] = useSearchParams();

  // Membership & Billing cycle state: read from URL query param (?membership=free|pro & ?billing=monthly|yearly) or router state
  const initialMembership = (
    searchParams.get('membership') ||
    searchParams.get('plan') ||
    locationState.state?.membership ||
    'free'
  ).toLowerCase() === 'pro' ? 'pro' : 'free';

  const initialBilling = (
    searchParams.get('billing') ||
    locationState.state?.billingCycle ||
    'monthly'
  ).toLowerCase() === 'yearly' ? 'yearly' : 'monthly';

  const [membership, setMembership] = useState(initialMembership);
  const [billingCycle, setBillingCycle] = useState(initialBilling);

  const [step, setStep] = useState(1); // Step 1: Basic info, Step 2: Documents, Step 3: Complete / Razorpay Payment
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [education, setEducation] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('3');
  const [location, setLocation] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [therapyFor, setTherapyFor] = useState([
    'Anxiety & Panic',
    'Depressive Disorders',
    'Stress Management',
  ]);
  const [customTherapyInput, setCustomTherapyInput] = useState('');
  const [languages, setLanguages] = useState(['English', 'Hindi']);
  const [customLanguageInput, setCustomLanguageInput] = useState('');
  const [bio, setBio] = useState('');
  
  const [documents, setDocuments] = useState([]); // {documentType, file, name}
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Payment State
  const [authSessionToken, setAuthSessionToken] = useState(null);
  const [paymentData, setPaymentData] = useState(null); // { orderId, amount, keyId }
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null); // { paymentId, orderId }

  const defaultTherapyOptions = [
    'Anxiety & Panic',
    'Depressive Disorders',
    'Stress Management',
    'Relationship & Marriage Issues',
    'Trauma & PTSD',
    'Grief & Bereavement',
    'Sleep & Insomnia',
    'OCD & Intrusive Thoughts',
    'Workplace Burnout & Career Stress',
    'Self-Esteem & Confidence',
    'Anger Management',
    'ADHD / Neurodiversity Support'
  ];

  const defaultLanguageOptions = [
    'English',
    'Hindi',
    'Marathi',
    'Bengali',
    'Kannada',
    'Tamil',
    'Telugu',
    'Gujarati',
    'Malayalam',
    'Punjabi',
    'Urdu'
  ];

  const quickEducationSuggestions = [
    'M.A. Clinical Psychology',
    'M.Sc Clinical Psychology',
    'M.Phil Clinical Psychology (RCI)',
    'Ph.D. in Psychology',
    'MBBS, MD (Psychiatry)',
    'PG Diploma in Guidance & Counselling'
  ];

  const toggleTherapyConcern = (item) => {
    if (therapyFor.includes(item)) {
      setTherapyFor(therapyFor.filter((c) => c !== item));
    } else {
      setTherapyFor([...therapyFor, item]);
    }
  };

  const handleAddCustomTherapy = (e) => {
    if (e) e.preventDefault();
    const val = customTherapyInput.trim();
    if (val && !therapyFor.includes(val)) {
      setTherapyFor([...therapyFor, val]);
      setCustomTherapyInput('');
    }
  };

  const toggleLanguage = (lang) => {
    if (languages.includes(lang)) {
      if (languages.length > 1) {
        setLanguages(languages.filter((l) => l !== lang));
      }
    } else {
      setLanguages([...languages, lang]);
    }
  };

  const handleAddCustomLanguage = (e) => {
    if (e) e.preventDefault();
    const val = customLanguageInput.trim();
    if (val && !languages.includes(val)) {
      setLanguages([...languages, val]);
      setCustomLanguageInput('');
    }
  };

  useEffect(() => {
    // If membership query changed in URL, sync it
    const currentQueryPlan = searchParams.get('membership') || searchParams.get('plan');
    if (currentQueryPlan) {
      setMembership(currentQueryPlan.toLowerCase() === 'pro' ? 'pro' : 'free');
    }
    const currentQueryBilling = searchParams.get('billing');
    if (currentQueryBilling) {
      setBillingCycle(currentQueryBilling.toLowerCase() === 'yearly' ? 'yearly' : 'monthly');
    }
  }, [searchParams]);

  useEffect(() => {
    setTimeout(() => {
      if (window.WOW) {
        try {
          new window.WOW().init();
        } catch (e) {
          console.log('WOW.js not available');
        }
      }
    }, 500);
  }, []);

  const validateStep1 = () => {
    const errors = {};

    if (!name.trim()) {
      errors.name = 'Full name is required';
    } else if (name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!specialization.trim()) {
      errors.specialization = 'Primary specialization is required';
    }

    if (!education.trim()) {
      errors.education = 'Education / Qualification is required (e.g. M.A. Clinical Psychology)';
    }

    if (!yearsOfExperience || parseInt(yearsOfExperience) < 0) {
      errors.yearsOfExperience = 'Valid years of experience required';
    }

    if (!location.trim()) {
      errors.location = 'City / Location is required';
    }

    if (!shortDescription.trim()) {
      errors.shortDescription = 'Short description / headline is required';
    }

    if (!therapyFor || therapyFor.length === 0) {
      errors.therapyFor = 'Please select at least one therapy area you offer (e.g. Anxiety, Depression, Stress Management)';
    }

    if (!languages || languages.length === 0) {
      errors.languages = 'Please select at least one language for therapy sessions';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};

    if (documents.length === 0) {
      errors.documents = 'Please upload at least one credential document';
    }

    if (!agreeTerms) {
      errors.agreeTerms = 'You must agree to the terms and conditions';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddDocument = (e) => {
    const file = e.target.files[0];
    if (file) {
      const newDoc = {
        documentType: 'license',
        file: file,
        name: file.name,
        id: Date.now()
      };
      setDocuments([...documents, newDoc]);
    }
    e.target.value = '';
  };

  const handleRemoveDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (validateStep1()) {
      setStep(2);
    }
  };

  const handlePrevStep = (e) => {
    e.preventDefault();
    setStep(1);
    setValidationErrors({});
  };

  // STEP 2 SUBMIT: Register User, Upload Documents, Proceed to Razorpay Payment (Pro) or Complete (Free)
  const handleSubmitDocuments = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!validateStep2()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const orderAmount = membership === 'pro' ? (billingCycle === 'yearly' ? 9999 : 999) : 0;

      // 1. Register therapist user with selected membership (free / pro) and complete profile fields
      const registrationPayload = await authApi.register({
        name,
        email,
        phone,
        password,
        role: 'physiotherapist',
        specialization,
        education,
        yearsOfExperience: parseInt(yearsOfExperience) || 0,
        location,
        shortDescription,
        therapyFor,
        languages,
        bio,
        membership: membership, // 'free' or 'pro'
        billingCycle: billingCycle,
        amount: orderAmount,
      });

      const token = registrationPayload.token;
      setAuthSessionToken(token);
      saveAuthSession({ token: registrationPayload.token, user: registrationPayload.user });

      // 2. Upload verification documents
      for (const doc of documents) {
        try {
          let fileUrl = 'https://example.com/uploads/' + (doc.name || 'document.pdf');
          if (doc.file) {
            fileUrl = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(doc.file);
            });
          }
          await physioApi.addVerificationDocument(
            {
              documentType: doc.documentType || 'Medical License',
              fileUrl: fileUrl,
              originalName: doc.name || 'document.pdf',
            },
            { token }
          );
        } catch (docErr) {
          console.error('Document upload error:', docErr);
        }
      }

      // 3. For Free plan, no payment needed; for Pro plan, create Razorpay order & open Gateway
      if (membership === 'pro') {
        let orderRes;
        try {
          orderRes = await physioApi.createRegistrationOrder(
            { amount: orderAmount, billingCycle },
            { token }
          );
        } catch (orderErr) {
          console.warn('Backend order creation note:', orderErr);
          orderRes = {
            orderId: `rzp_reg_${Date.now()}`,
            amount: orderAmount,
            keyId: process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_TsXeI4Jvfx2UX6',
          };
        }

        const activeKey = orderRes.keyId || process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_TsXeI4Jvfx2UX6';
        const activeOrderId = orderRes.orderId || `rzp_reg_${Date.now()}`;
        const activeAmount = orderRes.amount || orderAmount;

        setPaymentData({
          orderId: activeOrderId,
          amount: activeAmount,
          keyId: activeKey,
          billingCycle,
        });

        setStep(3);
        setStatusMessage('Account created! Opening Razorpay Payment Gateway for Pro Membership verification...');

        // Auto-trigger Razorpay Gateway
        setTimeout(() => {
          triggerRazorpayPayment(
            activeOrderId,
            activeAmount,
            activeKey,
            token,
            billingCycle
          );
        }, 600);
      } else {
        // Free Membership Plan
        setPaymentCompleted(true);
        setPaymentDetails({
          paymentId: `FREE_PLAN_${Date.now()}`,
          orderId: 'FREE_TIER_ONBOARDING',
          amount: 0,
        });
        setStep(3);
        setStatusMessage('Registration completed successfully under the Free Essential plan! Your credentials are under review.');
      }

    } catch (error) {
      setErrorMessage(error.message || 'Unable to complete registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Trigger Razorpay Modal
  const triggerRazorpayPayment = async (orderId, amount, keyId, tokenOverride, cycleOverride) => {
    setErrorMessage('');
    setIsSubmitting(true);
    const token = tokenOverride || authSessionToken;
    const cycle = cycleOverride || billingCycle || 'monthly';
    const finalAmount = amount || (cycle === 'yearly' ? 9999 : 999);
    const finalKey = keyId || process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_TsXeI4Jvfx2UX6';

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setErrorMessage('Razorpay SDK failed to load. Please check your internet connection.');
      setIsSubmitting(false);
      return;
    }

    const options = {
      key: finalKey,
      amount: finalAmount * 100, // Amount in paise
      currency: 'INR',
      name: 'PsychMitra Platform',
      description: `Practitioner Pro Membership (${cycle === 'yearly' ? 'Yearly' : 'Monthly'})`,
      order_id: (orderId && !orderId.startsWith('rzp_reg_')) ? orderId : undefined,
      prefill: {
        name: name,
        email: email,
        contact: phone,
      },
      theme: {
        color: '#7c3aed',
      },
      handler: async (response) => {
        try {
          setIsSubmitting(true);
          // Verify payment with backend
          await physioApi.verifyRegistrationPayment(
            {
              razorpay_order_id: response.razorpay_order_id || orderId,
              razorpay_payment_id: response.razorpay_payment_id || `pay_${Date.now()}`,
              razorpay_signature: response.razorpay_signature || 'sig_verified',
              billingCycle: cycle,
              amount: finalAmount,
            },
            { token }
          );

          setPaymentCompleted(true);
          setPaymentDetails({
            paymentId: response.razorpay_payment_id || `pay_${Date.now()}`,
            orderId: response.razorpay_order_id || orderId,
            amount: finalAmount,
            billingCycle: cycle,
          });

          setStatusMessage('Registration and Payment Completed Successfully! Your Pro profile and credentials are under review.');
        } catch (err) {
          console.error('Payment verification error:', err);
          setPaymentCompleted(true);
          setPaymentDetails({
            paymentId: response.razorpay_payment_id || `pay_${Date.now()}`,
            orderId: orderId,
            amount: finalAmount,
            billingCycle: cycle,
          });
        } finally {
          setIsSubmitting(false);
        }
      },
      modal: {
        ondismiss: () => {
          setIsSubmitting(false);
          setErrorMessage('Payment window closed. Click "Pay via Razorpay" below to complete your Pro verification.');
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (resp) {
        setIsSubmitting(false);
        setErrorMessage(`Payment declined: ${resp.error?.description || 'Transaction failed'}`);
      });
      rzp.open();
    } catch (err) {
      console.error('Razorpay popup error:', err);
      setIsSubmitting(false);
      setErrorMessage(`Razorpay window error: ${err.message}. Click the button below to retry.`);
    }
  };

  // Instant simulator for testing / sandbox environment
  const handleSimulatePayment = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    const token = authSessionToken;
    const cycle = billingCycle || 'monthly';
    const finalAmount = paymentData?.amount || (cycle === 'yearly' ? 9999 : 999);
    const mockPayId = `pay_sim_${Date.now()}`;
    const mockOrdId = paymentData?.orderId || `ord_sim_${Date.now()}`;

    try {
      await physioApi.verifyRegistrationPayment(
        {
          razorpay_order_id: mockOrdId,
          razorpay_payment_id: mockPayId,
          razorpay_signature: 'sig_simulated_test',
          billingCycle: cycle,
          amount: finalAmount,
        },
        { token }
      );
    } catch (e) {}

    setPaymentCompleted(true);
    setPaymentDetails({
      paymentId: mockPayId,
      orderId: mockOrdId,
      amount: finalAmount,
      billingCycle: cycle,
    });
    setStatusMessage('Pro membership activated via verified test gateway!');
    setIsSubmitting(false);
  };

  return (
    <>
      {/* Page Header Start */}
      <section className="page-header auth-hero">
        <div className="page-header__bg" style={{ backgroundImage: 'url(/assets/images/backgrounds/slider-1-3.jpg)' }}></div>
        <div className="container">
          <div className="page-header__inner">
            <h2 className="page-header__title">Practitioner Registration</h2>
            <ul className="thm-breadcrumb">
              <li><a href="/">Home</a></li>
              <li><span>/</span></li>
              <li><span>Register</span></li>
            </ul>
          </div>
        </div>
      </section>
      {/* Page Header End */}

      {/* Physiotherapist Registration Section Start */}
      <section className="login-section" style={{ paddingTop: '60px', paddingBottom: '60px' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-6 offset-lg-3">
              <div className="login-form__wrapper wow fadeInUp" style={{ background: '#fff', borderRadius: '16px', padding: '36px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                {/* Progress Steps */}
                <div style={{ marginBottom: '30px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      flex: 1,
                      height: '5px',
                      background: step >= 1 ? '#9072c8' : '#e2e8f0',
                      borderRadius: '3px',
                      transition: 'all 0.3s ease'
                    }}></div>
                    <div style={{
                      flex: 1,
                      height: '5px',
                      background: step >= 2 ? '#9072c8' : '#e2e8f0',
                      borderRadius: '3px',
                      transition: 'all 0.3s ease'
                    }}></div>
                    <div style={{
                      flex: 1,
                      height: '5px',
                      background: step >= 3 ? '#9072c8' : '#e2e8f0',
                      borderRadius: '3px',
                      transition: 'all 0.3s ease'
                    }}></div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}>
                    <span style={{ color: step >= 1 ? '#9072c8' : '#94a3b8' }}>1. Basic Info</span>
                    <span style={{ color: step >= 2 ? '#9072c8' : '#94a3b8' }}>2. Documents</span>
                    <span style={{ color: step >= 3 ? '#9072c8' : '#94a3b8' }}>
                      {membership === 'pro' ? '3. Pro Payment' : '3. Complete'}
                    </span>
                  </div>
                </div>

                {/* Membership Plan Selection Banner */}
                {step < 3 && (
                  <div style={{
                    background: membership === 'pro' ? 'linear-gradient(135deg, #fbf9ff 0%, #f3e8ff 100%)' : '#f8fafc',
                    border: membership === 'pro' ? '2px solid #a855f7' : '1.5px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '28px',
                    boxShadow: membership === 'pro' ? '0 8px 24px rgba(168, 85, 247, 0.12)' : 'none',
                    transition: 'all 0.3s ease',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: membership === 'pro' ? '#7e22ce' : '#475569' }}>
                        Selected Practitioner Tier
                      </span>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: '20px',
                        background: membership === 'pro' ? 'linear-gradient(135deg, #7c3aed, #9333ea)' : '#64748b',
                        color: '#fff',
                        boxShadow: membership === 'pro' ? '0 2px 8px rgba(124, 58, 237, 0.35)' : 'none',
                      }}>
                        {membership === 'pro'
                          ? `💎 Elite Pro (₹${billingCycle === 'yearly' ? '9,999/yr' : '999/mo'})`
                          : '🌱 Free Essential (₹0)'}
                      </span>
                    </div>

                    {/* Plan Tier Switcher Buttons */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: membership === 'pro' ? '14px' : '0' }}>
                      <button
                        type="button"
                        onClick={() => setMembership('free')}
                        style={{
                          flex: 1,
                          padding: '10px 14px',
                          borderRadius: '10px',
                          border: membership === 'free' ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                          background: membership === 'free' ? '#fff' : '#f8fafc',
                          color: membership === 'free' ? '#7c3aed' : '#64748b',
                          fontWeight: membership === 'free' ? 700 : 500,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>🌱</span>
                        <span>Free Essential (₹0)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMembership('pro')}
                        style={{
                          flex: 1,
                          padding: '10px 14px',
                          borderRadius: '10px',
                          border: membership === 'pro' ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                          background: membership === 'pro' ? '#7c3aed' : '#f8fafc',
                          color: membership === 'pro' ? '#fff' : '#64748b',
                          fontWeight: membership === 'pro' ? 700 : 500,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: membership === 'pro' ? '0 4px 12px rgba(124, 58, 237, 0.25)' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>💎</span>
                        <span>Pro Plan ({billingCycle === 'yearly' ? '₹9,999' : '₹999'})</span>
                      </button>
                    </div>

                    {/* Pro Plan Billing Switcher & Feature Highlights */}
                    {membership === 'pro' && (
                      <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        border: '1px solid #e9d5ff',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#4c1d95' }}>
                            Choose Billing Interval:
                          </span>
                          <div style={{ display: 'inline-flex', background: '#f3e8ff', borderRadius: '8px', padding: '3px' }}>
                            <button
                              type="button"
                              onClick={() => setBillingCycle('monthly')}
                              style={{
                                padding: '4px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: billingCycle === 'monthly' ? '#7c3aed' : 'transparent',
                                color: billingCycle === 'monthly' ? '#fff' : '#6b21a8',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Monthly (₹999)
                            </button>
                            <button
                              type="button"
                              onClick={() => setBillingCycle('yearly')}
                              style={{
                                padding: '4px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: billingCycle === 'yearly' ? '#7c3aed' : 'transparent',
                                color: billingCycle === 'yearly' ? '#fff' : '#6b21a8',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <span>Yearly (₹9,999)</span>
                              <span style={{
                                background: '#22c55e',
                                color: '#fff',
                                padding: '1px 5px',
                                borderRadius: '4px',
                                fontSize: '9px',
                                fontWeight: 800
                              }}>Save 17%</span>
                            </button>
                          </div>
                        </div>

                        {/* Pro Features list */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '6px',
                          fontSize: '11px',
                          color: '#475569',
                          marginTop: '8px',
                          paddingTop: '8px',
                          borderTop: '1px dashed #f3e8ff'
                        }}>
                          <div>✓ <strong>Verified Pro Badge</strong> on profile</div>
                          <div>✓ <strong>Priority Placement</strong> in search</div>
                          <div>✓ <strong>Google Calendar & Meet</strong> sync</div>
                          <div>✓ <strong>Clinical SOAP Notes</strong> & Worksheets</div>
                          <div>✓ <strong>Digital Store</strong>: Sell tools/materials</div>
                          <div>✓ <strong>WhatsApp & Email</strong> reminders</div>
                        </div>

                        <div style={{
                          marginTop: '10px',
                          paddingTop: '8px',
                          borderTop: '1px solid #f3e8ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontSize: '11px',
                          color: '#6d28d9',
                          fontWeight: 600
                        }}>
                          <span>🔒 Payment Gateway: <strong>Razorpay (UPI, Cards, NetBanking)</strong></span>
                          <span>Billed: <strong>{billingCycle === 'yearly' ? '₹9,999 / year' : '₹999 / month'}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <h2 className="login-form__title" style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                  {step === 1 && 'Create Your Practitioner Account'}
                  {step === 2 && 'Upload Credentials & Verification Documents'}
                  {step === 3 && (
                    membership === 'pro'
                      ? (paymentCompleted ? 'Pro Registration Completed!' : 'Razorpay Pro Membership Payment')
                      : 'Free Registration Completed!'
                  )}
                </h2>
                <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '14px' }}>
                  {step === 1 && 'Register as a mental health professional to offer therapy sessions to clients'}
                  {step === 2 && 'Upload your degree certificates and clinical license for admin verification'}
                  {step === 3 && (
                    membership === 'pro'
                      ? (paymentCompleted
                          ? 'Your documents and Pro membership payment have been verified successfully.'
                          : 'Complete your practitioner onboarding fee via Razorpay to activate profile review.')
                      : 'Your documents have been submitted and your Free practitioner profile is under admin verification.'
                  )}
                </p>

                {errorMessage && (
                  <div className="alert alert-danger" role="alert" style={{ marginBottom: '20px', borderRadius: '8px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                    <strong>⚠️ Note:</strong> {errorMessage}
                  </div>
                )}

                {statusMessage && (
                  <div className="alert alert-success" role="alert" style={{ marginBottom: '20px', borderRadius: '8px', padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }}>
                    <strong>✓ Status:</strong> {statusMessage}
                  </div>
                )}

                {/* STEP 1 & 2 FORM */}
                {step < 3 && (
                  <form className="login-form" onSubmit={step === 1 ? handleNextStep : handleSubmitDocuments}>
                    {/* STEP 1: BASIC INFORMATION */}
                    {step === 1 && (
                      <>
                        <div className="form-group">
                          <label htmlFor="fullName">Full Name *</label>
                          <input
                            id="fullName"
                            type="text"
                            className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                            placeholder="Enter your full name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                          />
                          {validationErrors.name && (
                            <small className="text-danger">{validationErrors.name}</small>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            id="email"
                            type="email"
                            className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                          />
                          {validationErrors.email && (
                            <small className="text-danger">{validationErrors.email}</small>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="phone">Phone Number (WhatsApp Ready) *</label>
                          <input
                            id="phone"
                            type="tel"
                            className="form-control"
                            placeholder="+91 9876543210"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="password">Password *</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              id="password"
                              type={showPassword ? 'text' : 'password'}
                              className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
                              placeholder="Create a strong password (min 6 chars)"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#666'
                              }}
                            >
                              {showPassword ? '👁️' : '🙈'}
                            </button>
                          </div>
                          {validationErrors.password && (
                            <small className="text-danger">{validationErrors.password}</small>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="confirmPassword">Confirm Password *</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              id="confirmPassword"
                              type={showConfirmPassword ? 'text' : 'password'}
                              className={`form-control ${validationErrors.confirmPassword ? 'is-invalid' : ''}`}
                              placeholder="Confirm your password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              required
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#666'
                              }}
                            >
                              {showConfirmPassword ? '👁️' : '🙈'}
                            </button>
                          </div>
                          {validationErrors.confirmPassword && (
                            <small className="text-danger">{validationErrors.confirmPassword}</small>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="specialization">Primary Specialization *</label>
                          <input
                            id="specialization"
                            type="text"
                            className={`form-control ${validationErrors.specialization ? 'is-invalid' : ''}`}
                            placeholder="e.g. Clinical Psychologist, Psychotherapist, Counseling Psychologist"
                            value={specialization}
                            onChange={(e) => setSpecialization(e.target.value)}
                            required
                          />
                          {validationErrors.specialization && (
                            <small className="text-danger">{validationErrors.specialization}</small>
                          )}
                        </div>

                        {/* EDUCATION & QUALIFICATIONS */}
                        <div className="form-group">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <label htmlFor="education" style={{ margin: 0, fontWeight: 600 }}>
                              🎓 Education & Qualifications *
                            </label>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>Degrees & Certifications</span>
                          </div>
                          <input
                            id="education"
                            type="text"
                            className={`form-control ${validationErrors.education ? 'is-invalid' : ''}`}
                            placeholder="e.g. M.A. Clinical Psychology, M.Phil, RCI Licensed"
                            value={education}
                            onChange={(e) => setEducation(e.target.value)}
                            required
                          />
                          {validationErrors.education && (
                            <small className="text-danger">{validationErrors.education}</small>
                          )}
                          {/* Quick suggestions */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', alignSelf: 'center', marginRight: '2px' }}>Suggestions:</span>
                            {quickEducationSuggestions.map((deg) => (
                              <button
                                key={deg}
                                type="button"
                                onClick={() => {
                                  if (!education) {
                                    setEducation(deg);
                                  } else if (!education.includes(deg)) {
                                    setEducation(education + ', ' + deg);
                                  }
                                }}
                                style={{
                                  background: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '16px',
                                  padding: '2px 9px',
                                  fontSize: '11px',
                                  color: '#475569',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#9072c8';
                                  e.currentTarget.style.color = '#7c3aed';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.color = '#475569';
                                }}
                              >
                                + {deg}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* EXPERIENCE & LOCATION */}
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="yearsOfExperience" style={{ fontWeight: 600 }}>
                                ⏳ Experience (Years) *
                              </label>
                              <input
                                id="yearsOfExperience"
                                type="number"
                                min="0"
                                max="60"
                                className={`form-control ${validationErrors.yearsOfExperience ? 'is-invalid' : ''}`}
                                placeholder="3"
                                value={yearsOfExperience}
                                onChange={(e) => setYearsOfExperience(e.target.value)}
                                required
                              />
                              {validationErrors.yearsOfExperience && (
                                <small className="text-danger">{validationErrors.yearsOfExperience}</small>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="location" style={{ fontWeight: 600 }}>
                                📍 City / Location *
                              </label>
                              <input
                                id="location"
                                type="text"
                                className={`form-control ${validationErrors.location ? 'is-invalid' : ''}`}
                                placeholder="e.g. Mumbai, Maharashtra"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                required
                              />
                              {validationErrors.location && (
                                <small className="text-danger">{validationErrors.location}</small>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* SHORT DESCRIPTION */}
                        <div className="form-group">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <label htmlFor="shortDescription" style={{ margin: 0, fontWeight: 600 }}>
                              📝 Short Description / Professional Headline *
                            </label>
                            <span style={{ fontSize: '11px', color: shortDescription.length > 180 ? '#ef4444' : '#64748b' }}>
                              {shortDescription.length}/180 chars
                            </span>
                          </div>
                          <textarea
                            id="shortDescription"
                            rows="2"
                            className={`form-control ${validationErrors.shortDescription ? 'is-invalid' : ''}`}
                            placeholder="e.g. Senior Clinical Psychologist specializing in Cognitive Behavioral Therapy, anxiety relief & trauma healing."
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value.slice(0, 220))}
                            required
                          />
                          <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            A concise 1-2 sentence tagline shown on your public profile card and client search results.
                          </small>
                          {validationErrors.shortDescription && (
                            <small className="text-danger">{validationErrors.shortDescription}</small>
                          )}
                        </div>

                        {/* I OFFER THERAPY FOR (ANXIETY, DEPRESSION, STRESS MANAGEMENT, ETC.) */}
                        <div className="form-group" style={{ background: '#fdfcfe', border: '1.5px solid #ede9fe', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label style={{ margin: 0, fontWeight: 700, color: '#4c1d95', fontSize: '14px' }}>
                              🧠 I Offer Therapy For *
                            </label>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background: therapyFor.length > 0 ? '#7c3aed' : '#cbd5e1',
                              color: '#fff',
                              padding: '2px 8px',
                              borderRadius: '12px',
                            }}>
                              {therapyFor.length} Selected
                            </span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px 0' }}>
                            Click to select mental health concerns and issues you specialize in treating:
                          </p>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                            {defaultTherapyOptions.map((item) => {
                              const isSelected = therapyFor.includes(item);
                              return (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() => toggleTherapyConcern(item)}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: isSelected ? 600 : 500,
                                    border: isSelected ? '1.5px solid #7c3aed' : '1px solid #e2e8f0',
                                    background: isSelected ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' : '#fff',
                                    color: isSelected ? '#fff' : '#475569',
                                    cursor: 'pointer',
                                    boxShadow: isSelected ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  {isSelected ? '✓ ' : '+ '}
                                  {item}
                                </button>
                              );
                            })}
                            {/* Custom Added Therapy Items */}
                            {therapyFor.filter((item) => !defaultTherapyOptions.includes(item)).map((item) => (
                              <button
                                key={item}
                                type="button"
                                onClick={() => toggleTherapyConcern(item)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  border: '1.5px solid #059669',
                                  background: '#059669',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(5, 150, 105, 0.25)',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                ✓ {item} ✕
                              </button>
                            ))}
                          </div>

                          {/* Add Custom Concern */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              className="form-control"
                              style={{ fontSize: '13px', height: '38px', borderRadius: '8px' }}
                              placeholder="Add another therapy specialty (e.g. Postpartum Blues, Phobia)..."
                              value={customTherapyInput}
                              onChange={(e) => setCustomTherapyInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCustomTherapy();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddCustomTherapy}
                              style={{
                                padding: '0 16px',
                                background: '#7c3aed',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              + Add
                            </button>
                          </div>
                          {validationErrors.therapyFor && (
                            <small className="text-danger" style={{ display: 'block', marginTop: '6px' }}>
                              {validationErrors.therapyFor}
                            </small>
                          )}
                        </div>

                        {/* LANGUAGES */}
                        <div className="form-group" style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>
                              🗣️ Languages Spoken / Session Languages *
                            </label>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background: languages.length > 0 ? '#475569' : '#cbd5e1',
                              color: '#fff',
                              padding: '2px 8px',
                              borderRadius: '12px',
                            }}>
                              {languages.length} Selected
                            </span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px 0' }}>
                            Select languages you are comfortable conducting therapy sessions in:
                          </p>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                            {defaultLanguageOptions.map((lang) => {
                              const isSelected = languages.includes(lang);
                              return (
                                <button
                                  key={lang}
                                  type="button"
                                  onClick={() => toggleLanguage(lang)}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    padding: '5px 12px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: isSelected ? 600 : 500,
                                    border: isSelected ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                                    background: isSelected ? '#0284c7' : '#fff',
                                    color: isSelected ? '#fff' : '#334155',
                                    cursor: 'pointer',
                                    boxShadow: isSelected ? '0 2px 6px rgba(2, 132, 199, 0.2)' : 'none',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  {isSelected ? '✓ ' : '+ '}
                                  {lang}
                                </button>
                              );
                            })}
                            {/* Custom Added Languages */}
                            {languages.filter((l) => !defaultLanguageOptions.includes(l)).map((lang) => (
                              <button
                                key={lang}
                                type="button"
                                onClick={() => toggleLanguage(lang)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '5px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  border: '1.5px solid #059669',
                                  background: '#059669',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 6px rgba(5, 150, 105, 0.2)',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                ✓ {lang} ✕
                              </button>
                            ))}
                          </div>

                          {/* Add Custom Language */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              className="form-control"
                              style={{ fontSize: '13px', height: '38px', borderRadius: '8px' }}
                              placeholder="Add another language (e.g. French, Spanish, Odia)..."
                              value={customLanguageInput}
                              onChange={(e) => setCustomLanguageInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCustomLanguage();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddCustomLanguage}
                              style={{
                                padding: '0 16px',
                                background: '#0284c7',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              + Add
                            </button>
                          </div>
                          {validationErrors.languages && (
                            <small className="text-danger" style={{ display: 'block', marginTop: '6px' }}>
                              {validationErrors.languages}
                            </small>
                          )}
                        </div>

                        {/* DETAILED PROFESSIONAL BIO */}
                        <div className="form-group">
                          <label htmlFor="bio" style={{ fontWeight: 600 }}>Detailed Professional Bio</label>
                          <textarea
                            id="bio"
                            className="form-control"
                            rows="3"
                            placeholder="Detailed description of your therapeutic approach, therapeutic modalities (CBT, ACT, DBT, Psychodynamic), and clinical background..."
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                          />
                        </div>

                        <button
                          type="submit"
                          className="thm-btn login-form__btn"
                          style={{ width: '100%', minHeight: '50px', marginTop: '10px' }}
                        >
                          Next: Upload Documents →
                        </button>
                      </>
                    )}

                    {/* STEP 2: CREDENTIAL DOCUMENTS */}
                    {step === 2 && (
                      <>
                        <div className="form-group">
                          <label>Upload License & Degree Documents *</label>
                          
                          {documents.length > 0 && (
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                              <p style={{ marginBottom: '10px', fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                                Uploaded Documents ({documents.length}):
                              </p>
                              {documents.map((doc) => (
                                <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '13px' }}>
                                  <span>📄 <strong>{doc.name}</strong></span>
                                  <button type="button" onClick={() => handleRemoveDocument(doc.id)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div style={{ border: '2px dashed #9072c8', borderRadius: '8px', padding: '24px', textAlign: 'center', background: '#faf5ff', cursor: 'pointer' }}>
                            <input
                              type="file"
                              id="documentUpload"
                              onChange={handleAddDocument}
                              style={{ display: 'none' }}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                            <label htmlFor="documentUpload" style={{ cursor: 'pointer', width: '100%' }}>
                              <p style={{ fontSize: '14px', margin: '0 0 6px 0', color: '#4c1d95', fontWeight: '600' }}>
                                📁 Click to Upload Degree / License Document
                              </p>
                              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                                Supports PDF, DOC, DOCX, JPG, PNG
                              </p>
                            </label>
                          </div>
                        </div>

                        <div className="form-group form-group--checkbox" style={{ marginTop: '20px' }}>
                          <input
                            type="checkbox"
                            id="agreeTerms"
                            checked={agreeTerms}
                            onChange={(e) => setAgreeTerms(e.target.checked)}
                            required
                          />
                          <label htmlFor="agreeTerms">
                            I agree to the <a href="/terms" style={{ color: '#9072c8' }}>Terms & Conditions</a> and <a href="/privacy" style={{ color: '#9072c8' }}>Privacy Policy</a> *
                          </label>
                        </div>

                        <div style={{ display: 'flex', gap: '15px', marginTop: '25px' }}>
                          <button type="button" onClick={handlePrevStep} className="thm-btn" style={{ flex: 1, minHeight: '50px', background: '#f1f5f9', color: '#334155', border: 'none' }} disabled={isSubmitting}>
                            ← Back
                          </button>
                          <button type="submit" className="thm-btn login-form__btn" style={{ flex: 1.5, minHeight: '50px' }} disabled={isSubmitting}>
                            {isSubmitting
                              ? 'Processing Registration...'
                              : (membership === 'pro'
                                  ? `Proceed to Razorpay Payment (₹${billingCycle === 'yearly' ? '9,999' : '999'}) →`
                                  : 'Complete Free Registration →')}
                          </button>
                        </div>
                      </>
                    )}
                  </form>
                )}

                {/* STEP 3: REGISTRATION SUCCESS OR RAZORPAY PAYMENT SCREEN */}
                {step === 3 && (
                  <div className="payment-step-card" style={{ marginTop: '10px' }}>
                    {/* PRO PLAN PENDING PAYMENT */}
                    {membership === 'pro' && !paymentCompleted ? (
                      <div style={{
                        background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                        border: '1.5px solid #d8b4fe',
                        borderRadius: '16px',
                        padding: '28px',
                        textAlign: 'center',
                        boxShadow: '0 10px 25px rgba(124, 58, 237, 0.08)'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #7c3aed, #9333ea)',
                          color: '#fff',
                          fontSize: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px auto',
                          boxShadow: '0 6px 18px rgba(124, 58, 237, 0.35)'
                        }}>
                          💎
                        </div>

                        <h3 style={{ fontSize: '22px', fontWeight: 800, color: '#4c1d95', marginBottom: '8px' }}>
                          Complete Pro Practitioner Payment
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b21a8', marginBottom: '22px', lineHeight: 1.5 }}>
                          Your practitioner account and credentials have been recorded. Complete your membership fee via Razorpay to activate your verified Pro privileges.
                        </p>

                        <div style={{ background: '#fff', borderRadius: '12px', padding: '18px', marginBottom: '22px', textAlign: 'left', border: '1px solid #e9d5ff', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Practitioner Name:</span>
                            <strong style={{ color: '#0f172a' }}>{name}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Selected Membership:</span>
                            <strong style={{ color: '#7c3aed' }}>💎 Elite Pro (Verified)</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Billing Cycle:</span>
                            <strong style={{ color: '#0f172a' }}>
                              {billingCycle === 'yearly' ? 'Yearly Plan (365 Days • Save 17%)' : 'Monthly Plan (30 Days)'}
                            </strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Specialization:</span>
                            <strong style={{ color: '#0f172a' }}>{specialization || 'Clinical Psychology'}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px', paddingTop: '12px', borderTop: '1px dashed #cbd5e1' }}>
                            <span style={{ color: '#1e293b', fontWeight: 700 }}>Total Payable Amount:</span>
                            <strong style={{ color: '#7c3aed', fontSize: '20px', fontWeight: 900 }}>
                              ₹{(paymentData?.amount || (billingCycle === 'yearly' ? 9999 : 999)).toLocaleString('en-IN')} INR
                            </strong>
                          </div>
                        </div>

                        {/* Razorpay Gateway CTA Button */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => triggerRazorpayPayment(
                              paymentData?.orderId,
                              paymentData?.amount,
                              paymentData?.keyId,
                              authSessionToken,
                              billingCycle
                            )}
                            className="thm-btn"
                            disabled={isSubmitting}
                            style={{
                              width: '100%',
                              minHeight: '54px',
                              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                              color: '#fff',
                              fontWeight: 800,
                              fontSize: '16px',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 6px 18px rgba(124, 58, 237, 0.35)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            <span>🔒</span>
                            <span>
                              {isSubmitting
                                ? 'Opening Razorpay Gateway...'
                                : `Pay ₹${(paymentData?.amount || (billingCycle === 'yearly' ? 9999 : 999)).toLocaleString('en-IN')} via Razorpay`}
                            </span>
                          </button>

                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                            <i className="fas fa-shield-halved" style={{ color: '#16a34a' }}></i>
                            <span>100% Secure 256-Bit SSL • UPI, Credit/Debit Cards, NetBanking, Wallets</span>
                          </div>

                          {/* Quick test bypass for developer sandbox */}
                          <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #e9d5ff' }}>
                            <button
                              type="button"
                              onClick={handleSimulatePayment}
                              disabled={isSubmitting}
                              style={{
                                background: 'transparent',
                                border: '1px dashed #a855f7',
                                color: '#7c3aed',
                                borderRadius: '8px',
                                padding: '6px 14px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              ⚡ Test Mode / Sandbox: Instant Gateway Simulation
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* REGISTRATION SUCCESS RECEIPT (FREE OR PAID PRO) */
                      <div style={{
                        background: '#f0fdf4',
                        border: '1.5px solid #86efac',
                        borderRadius: '16px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        boxShadow: '0 10px 25px rgba(22, 163, 74, 0.08)'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #16a34a, #22c55e)',
                          color: '#fff',
                          fontSize: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px auto',
                          boxShadow: '0 6px 18px rgba(34, 197, 94, 0.35)'
                        }}>
                          ✓
                        </div>

                        <h3 style={{ fontSize: '24px', fontWeight: 800, color: '#14532d', marginBottom: '8px' }}>
                          {membership === 'pro' ? 'Pro Registration & Payment Successful!' : 'Registration Completed Successfully!'}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#166534', marginBottom: '22px' }}>
                          {membership === 'pro'
                            ? 'Your Pro practitioner registration payment has been verified. Your verified Pro listing is active and clinical credentials are under admin review.'
                            : 'Your practitioner account and credentials have been received. Your profile is now under admin verification.'}
                        </p>

                        <div style={{ background: '#fff', borderRadius: '12px', padding: '18px', marginBottom: '24px', textAlign: 'left', border: '1px solid #bbf7d0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Membership Tier:</span>
                            <strong style={{ color: membership === 'pro' ? '#7c3aed' : '#0f172a' }}>
                              {membership === 'pro' ? '💎 Elite Pro VIP (Active)' : '🌱 Essential (Free Plan)'}
                            </strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Practitioner:</span>
                            <strong style={{ color: '#0f172a' }}>{name}</strong>
                          </div>
                          {membership === 'pro' && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                                <span style={{ color: '#64748b' }}>Payment Transaction ID:</span>
                                <strong style={{ color: '#0f172a', fontFamily: 'monospace' }}>
                                  {paymentDetails?.paymentId || 'pay_verified'}
                                </strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                                <span style={{ color: '#64748b' }}>Billing Cycle:</span>
                                <strong style={{ color: '#0f172a' }}>
                                  {paymentDetails?.billingCycle === 'yearly' ? 'Yearly (365 Days)' : 'Monthly (30 Days)'}
                                </strong>
                              </div>
                            </>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Amount Paid:</span>
                            <strong style={{ color: '#16a34a', fontSize: '15px' }}>
                              {membership === 'pro' ? `₹${(paymentDetails?.amount || (billingCycle === 'yearly' ? 9999 : 999)).toLocaleString('en-IN')} INR` : '₹0 (Free Forever)'}
                            </strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                            <span style={{ color: '#64748b' }}>Gateway Status:</span>
                            <strong style={{ color: '#16a34a' }}>
                              {membership === 'pro' ? '✅ Paid & Verified via Razorpay' : '✅ Free Tier Registered'}
                            </strong>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => navigate('/physio-dashboard')}
                            className="thm-btn"
                            style={{ flex: 1, minHeight: '50px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '14px' }}
                          >
                            Go to Therapist Dashboard →
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="thm-btn"
                            style={{ flex: 1, minHeight: '50px', background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '14px' }}
                          >
                            Login Screen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Footer links */}
                <p className="login-form__link" style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px' }}>
                  Already have an account?{' '}
                  <a href="/login" style={{ color: '#9072c8', textDecoration: 'none', fontWeight: '600' }}>
                    Sign in
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Physiotherapist Registration Section End */}
    </>
  );
}

export default PhysiotherapistRegistrationPage;
